/**
 * Location.kind from the API may be a string (Rails enum), a legacy symbol-style value, or
 * a numeric database value (0=office, 1=virtual, 2=home). Public booking UI must treat
 * these consistently and prefer in-office locations over in-home when choosing a default.
 */
const KIND_ENUM_NUMERIC: Record<string, string> = {
  "0": "office",
  "1": "virtual",
  "2": "home"
}

export function normalizeWebsiteLocationKind(kind: unknown): string {
  if (kind === null || kind === undefined) {
    return ""
  }
  const asString = String(kind).trim().toLowerCase().replace(/^:/, "")
  if (KIND_ENUM_NUMERIC[asString]) {
    return KIND_ENUM_NUMERIC[asString]
  }
  return asString
}

function defaultBookingPriority(normalizedKind: string): number {
  if (normalizedKind === "office" || normalizedKind === "physical") {
    return 0
  }
  if (normalizedKind === "virtual") {
    return 1
  }
  if (normalizedKind === "home") {
    return 2
  }
  return 99
}

/**
 * Picks the location to pre-select for booking: office/physical first, then virtual, then home.
 * Avoids using locations[0] alone, which can be an in-home row ahead of the practitioner's office.
 */
export function pickDefaultBookingLocation<T extends { kind?: unknown }>(
  locations: T[] | null | undefined
): T | undefined {
  if (!locations?.length) {
    return undefined
  }

  const scored = locations.map((loc, index) => {
    const normalized = normalizeWebsiteLocationKind(loc.kind)
    const priority = defaultBookingPriority(normalized)

    return { loc, index, priority }
  })

  scored.sort((a, b) => {
    const pa = a.priority
    const pb = b.priority
    if (pa !== pb) {
      return pa - pb
    }
    return a.index - b.index
  })

  return scored[0]?.loc
}
